import { useEffect, useRef, useState, type DragEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ConfirmModal, Loader, useToast } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { usePermissions } from '@/hooks/usePermissions';
import { CourseHubShell } from '@/components/courses/CourseHubShell';
import { api } from '@/lib/api';
import { builderSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import { libraryCreateUrl, libraryShowQueryFromCourseBuilder } from '@/lib/library-navigation';
import { AddContentModal } from './AddContentModal';
import { CourseBuilderItemSettingsModal } from './CourseBuilderItemSettingsModal';
import { CourseBuilderLabelModal } from './CourseBuilderLabelModal';
import {
  cloneSections,
  itemRestrictionBanners,
  moduleIcon,
  moduleNumber,
  sectionsFromResponse,
  type BuilderItem,
  type BuilderResponse,
  type BuilderSection,
  type BuilderAssessmentInstance,
  type BuilderH5pInstance,
} from './course-builder-types';

interface DragState {
  sectionIndex: number;
  itemIndex: number;
}

export function CourseBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = usePermissions();
  const canEditBuilder = hasPermission(PERMISSIONS.CONSTRUCTOR_EDITAR);
  const [sections, setSections] = useState<BuilderSection[]>([]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [librarySectionId, setLibrarySectionId] = useState<string | null>(null);
  const [labelSectionId, setLabelSectionId] = useState<string | null>(null);
  const [labelSaving, setLabelSaving] = useState(false);
  const [insertingObject, setInsertingObject] = useState(false);
  const [selectedItem, setSelectedItem] = useState<BuilderItem | null>(null);
  const [dragItem, setDragItem] = useState<DragState | null>(null);
  const [dragSectionIndex, setDragSectionIndex] = useState<number | null>(null);
  const [sectionDropIndex, setSectionDropIndex] = useState<number | null>(null);
  const dragSectionIndexRef = useRef<number | null>(null);
  const [pendingRemoval, setPendingRemoval] = useState<
    | { kind: 'section'; sectionId: string; sectionIndex: number }
    | { kind: 'item'; item: BuilderItem; sectionIndex: number }
    | null
  >(null);
  const [removing, setRemoving] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { toast } = useToast();

  const { data: course } = useQuery({
    queryKey: ['courses', id],
    queryFn: () => api<{ id: string; name: string; category?: { id: string } }>(`/courses/${id}`),
    enabled: Boolean(id),
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['courses', id, 'builder'],
    queryFn: () => api<BuilderResponse>(`/courses/${id}/builder`),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (!data) return;
    const loaded = sectionsFromResponse(data);
    setSections(cloneSections(loaded));
    setCollapsed(Object.fromEntries(loaded.map((section) => [section.id, false])));
    setDirty(false);
  }, [data]);

  useEffect(() => {
    if (!menuOpen) return;
    function handlePointerDown(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(null);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [menuOpen]);

  const courseName = course?.name ?? 'Curso';

  function markDirty() {
    setDirty(true);
  }

  function updateSections(updater: (prev: BuilderSection[]) => BuilderSection[]) {
    setSections((prev) => updater(prev));
    markDirty();
  }

  async function persistItem(item: BuilderItem): Promise<BuilderItem | undefined> {
    if (!id) return undefined;
    const response = await api<{ item: BuilderItem }>(`/courses/${id}/builder/items/${item.id}`, {
      method: 'PATCH',
      body: {
        titleOverride: item.titleOverride ?? item.displayTitle,
        isVisible: item.isVisible !== false,
        settings: {
          available_from: item.availableFrom || null,
          available_until: item.availableUntil || null,
          requires_item_id: item.requiresItemId ?? null,
        },
      },
    });

    let mergedItem: BuilderItem = response.item;

    if (item.typeValue === 'h5p' && item.h5pInstance) {
      const h5pResponse = await api<{ h5pInstance: BuilderH5pInstance }>(
        `/courses/${id}/builder/items/${item.id}/h5p`,
        {
          method: 'PATCH',
          body: {
            isRequired: item.h5pInstance.isRequired === true,
            allowPreview: item.h5pInstance.allowPreview !== false,
            unlimitedAttempts: item.h5pInstance.unlimitedAttempts !== false,
            showSolutions: item.h5pInstance.showSolutions === true,
            allowRetry: item.h5pInstance.allowRetry !== false,
            gradeWeight: item.h5pInstance.gradeWeight ?? null,
            maxAttempts:
              item.h5pInstance.unlimitedAttempts === false ? item.h5pInstance.maxAttempts ?? 1 : null,
          },
        },
      );
      mergedItem = { ...mergedItem, h5pInstance: h5pResponse.h5pInstance };
    }

    if (item.typeValue === 'assessment' && item.assessmentInstance) {
      const assessmentResponse = await api<{ assessmentInstance: BuilderAssessmentInstance }>(
        `/courses/${id}/builder/items/${item.id}/assessment`,
        {
          method: 'PATCH',
          body: {
            isRequired: item.assessmentInstance.isRequired === true,
            unlimitedAttempts: item.assessmentInstance.unlimitedAttempts !== false,
            maxAttempts:
              item.assessmentInstance.unlimitedAttempts === false
                ? item.assessmentInstance.maxAttempts ?? 1
                : null,
            timeLimitMinutes: item.assessmentInstance.timeLimitMinutes ?? null,
            gradeWeight: item.assessmentInstance.gradeWeight ?? null,
            opensAt: item.assessmentInstance.opensAt || null,
            closesAt: item.assessmentInstance.closesAt || null,
          },
        },
      );
      mergedItem = { ...mergedItem, assessmentInstance: assessmentResponse.assessmentInstance };
    }

    return mergedItem;
  }

  async function saveAll() {
    if (!id || saving) return;
    setSaving(true);
    try {
      await Promise.all(
        sections.map((section, index) =>
          api(`/courses/${id}/builder/sections/${section.id}`, {
            method: 'PATCH',
            body: { title: section.title, isVisible: section.isVisible !== false },
          }).then(() => {
            section.sortOrder = index;
          }),
        ),
      );

      await api(`/courses/${id}/builder/sections/reorder`, {
        method: 'POST',
        body: {
          sections: sections.map((section, index) => ({ id: section.id, sortOrder: index })),
        },
      });

      const items = sections.flatMap((section) =>
        section.items.map((item, index) => ({
          id: item.id,
          sortOrder: index,
          sectionId: section.id,
        })),
      );

      await api(`/courses/${id}/builder/items/reorder`, {
        method: 'POST',
        body: { items },
      });

      await Promise.all(sections.flatMap((section) => section.items.map((item) => persistItem(item))));

      setDirty(false);
      toast(builderSuccessToast('save'));
      await refetch();
    } catch (err) {
      toast(roleActionErrorToast(err, 'No se pudieron guardar los cambios'));
    } finally {
      setSaving(false);
    }
  }

  async function addSection() {
    if (!id) return;
    const response = await api<{ section: BuilderSection }>(`/courses/${id}/builder/sections`, {
      method: 'POST',
      body: { title: 'Nueva sección', isVisible: true },
    });
    setSections((prev) => [...prev, { ...response.section, items: response.section.items ?? [] }]);
    setCollapsed((prev) => ({ ...prev, [response.section.id]: false }));
    toast(builderSuccessToast('section-create'));
  }

  async function deleteSection(sectionId: string, sectionIndex: number) {
    if (!id || removing) return;
    setRemoving(true);
    try {
      await api(`/courses/${id}/builder/sections/${sectionId}`, { method: 'DELETE' });
      setSections((prev) => prev.filter((_, index) => index !== sectionIndex));
      if (selectedItem) {
        const stillExists = sections.some((section) => section.items.some((item) => item.id === selectedItem.id));
        if (!stillExists) setSelectedItem(null);
      }
      setPendingRemoval(null);
      toast(builderSuccessToast('section-delete'));
    } catch (err) {
      toast(roleActionErrorToast(err, 'No se pudo eliminar la sección'));
    } finally {
      setRemoving(false);
    }
  }

  async function toggleSectionVisibility(section: BuilderSection) {
    if (!id) return;
    const response = await api<{ section: BuilderSection }>(
      `/courses/${id}/builder/sections/${section.id}/toggle`,
      { method: 'PATCH' },
    );
    updateSections((prev) =>
      prev.map((entry) => (entry.id === section.id ? { ...entry, isVisible: response.section.isVisible } : entry)),
    );
    setDirty(false);
  }

  async function duplicateSection(section: BuilderSection) {
    if (!id) return;
    const response = await api<{ section: BuilderSection }>(
      `/courses/${id}/builder/sections/${section.id}/duplicate`,
      { method: 'POST' },
    );
    setSections((prev) => [...prev, { ...response.section, items: response.section.items ?? [] }]);
    setCollapsed((prev) => ({ ...prev, [response.section.id]: false }));
    toast(builderSuccessToast('section-duplicate'));
  }

  async function insertLearningObject(learningObjectId: string) {
    if (!id || !librarySectionId) return;
    setInsertingObject(true);
    try {
      const response = await api<{ item: BuilderItem }>(
        `/courses/${id}/builder/sections/${librarySectionId}/items`,
        { method: 'POST', body: { learningObjectId } },
      );
      updateSections((prev) =>
        prev.map((section) =>
          section.id === librarySectionId
            ? { ...section, items: [...section.items, response.item] }
            : section,
        ),
      );
      setLibrarySectionId(null);
      setDirty(false);
      toast(builderSuccessToast('activity-add'));
    } catch (err) {
      toast(roleActionErrorToast(err, 'No se pudo añadir la actividad'));
    } finally {
      setInsertingObject(false);
    }
  }

  async function insertLabel(text: string) {
    if (!id || !labelSectionId || !text.trim()) return;
    setLabelSaving(true);
    try {
      const response = await api<{ item: BuilderItem }>(
        `/courses/${id}/builder/sections/${labelSectionId}/labels`,
        { method: 'POST', body: { text } },
      );
      updateSections((prev) =>
        prev.map((section) =>
          section.id === labelSectionId
            ? { ...section, items: [...section.items, response.item] }
            : section,
        ),
      );
      setLabelSectionId(null);
      setDirty(false);
      toast(builderSuccessToast('label-add'));
    } catch (err) {
      toast(roleActionErrorToast(err, 'No se pudo añadir la etiqueta'));
    } finally {
      setLabelSaving(false);
    }
  }

  async function removeItem(item: BuilderItem, sectionIndex: number) {
    if (!id || removing) return;
    setRemoving(true);
    try {
      await api(`/courses/${id}/builder/items/${item.id}`, { method: 'DELETE' });
      updateSections((prev) =>
        prev.map((section, index) =>
          index === sectionIndex
            ? { ...section, items: section.items.filter((entry) => entry.id !== item.id) }
            : section,
        ),
      );
      if (selectedItem?.id === item.id) setSelectedItem(null);
      setDirty(false);
      setPendingRemoval(null);
      toast(builderSuccessToast('activity-delete'));
    } catch (err) {
      toast(roleActionErrorToast(err, 'No se pudo quitar la actividad'));
    } finally {
      setRemoving(false);
    }
  }

  function moveItem(sectionIndex: number, targetIndex: number) {
    if (!dragItem || dragItem.sectionIndex !== sectionIndex) return;
    if (dragItem.itemIndex === targetIndex) return;
    updateSections((prev) => {
      const next = cloneSections(prev);
      const items = next[sectionIndex].items;
      const [moved] = items.splice(dragItem.itemIndex, 1);
      items.splice(targetIndex, 0, moved);
      items.forEach((item, index) => {
        item.sortOrder = index;
      });
      return next;
    });
    setDragItem(null);
  }

  function clearSectionDrag() {
    dragSectionIndexRef.current = null;
    setDragSectionIndex(null);
    setSectionDropIndex(null);
  }

  function onSectionDragStart(sectionIndex: number, event: DragEvent<HTMLDivElement>) {
    event.stopPropagation();
    dragSectionIndexRef.current = sectionIndex;
    setDragSectionIndex(sectionIndex);
    setDragItem(null);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', `section:${sectionIndex}`);
    const card = event.currentTarget.closest('section');
    if (card) event.dataTransfer.setDragImage(card, 32, 28);
  }

  function onSectionDragOver(sectionIndex: number, event: DragEvent<HTMLElement>) {
    if (dragSectionIndexRef.current === null) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setSectionDropIndex((current) => (current === sectionIndex ? current : sectionIndex));
  }

  async function persistSectionOrder(next: BuilderSection[]) {
    if (!id) return;
    try {
      await api(`/courses/${id}/builder/sections/reorder`, {
        method: 'POST',
        body: {
          sections: next.map((section, index) => ({ id: section.id, sortOrder: index })),
        },
      });
    } catch (err) {
      toast(roleActionErrorToast(err, 'No se pudo reordenar la sección'));
      await refetch();
    }
  }

  function moveSection(fromIndex: number, targetIndex: number) {
    clearSectionDrag();
    if (fromIndex === targetIndex) return;
    if (fromIndex < 0 || fromIndex >= sections.length) return;

    const next = cloneSections(sections);
    const [moved] = next.splice(fromIndex, 1);
    if (!moved) return;
    next.splice(targetIndex, 0, moved);
    next.forEach((section, index) => {
      section.sortOrder = index;
    });
    setSections(next);
    void persistSectionOrder(next);
  }

  async function saveSelectedItemSettings(item: BuilderItem) {
    if (saving) return;
    setSaving(true);
    try {
      const updated = await persistItem(item);
      if (updated) {
        setSelectedItem(updated);
        setSections((prev) =>
          prev.map((section) => ({
            ...section,
            items: section.items.map((entry) => (entry.id === updated.id ? { ...updated } : entry)),
          })),
        );
        setDirty(false);
        toast(builderSuccessToast('settings'));
      }
    } catch (err) {
      toast(roleActionErrorToast(err, 'No se pudo guardar la configuración'));
    } finally {
      setSaving(false);
    }
  }

  const aside = (
    <div className="lms-course-hero__aside-actions">
      {canEditBuilder && (
        <button type="button" className="lms-course-hero__aside-save" onClick={saveAll} disabled={saving}>
          {!saving && (
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          )}
          <span>{saving ? 'Guardando…' : 'Guardar cambios'}</span>
        </button>
      )}
      <button
        type="button"
        className="lms-course-hero__aside-collapse"
        onClick={() => {
          const next: Record<string, boolean> = {};
          for (const section of sections) next[section.id] = true;
          setCollapsed(next);
        }}
      >
        Colapsar todo
      </button>
      {dirty && !saving && <span className="lms-course-hero__aside-dirty">Cambios sin guardar</span>}
    </div>
  );

  if (!id) return null;

  return (
    <CourseHubShell
      courseId={id}
      courseName={courseName}
      categoryId={course?.category?.id}
      activeTab="edicion"
      meta={
        <p className="mt-2 text-sm">
          Modo edición: añade, reordena y configura secciones y actividades del curso.
        </p>
      }
      aside={aside}
    >
      {error && (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader label="Cargando constructor…" />
        </div>
      ) : (
        <div className="lms-course-modules flex flex-col gap-6">
          {sections.length === 0 ? (
            <div className="lms-course-module rounded-xl border border-dashed border-dash-outline-variant p-8 text-center text-sm text-dash-on-surface-variant">
              <p>
                {canEditBuilder
                  ? 'Añade una sección para comenzar a construir el curso.'
                  : 'Este curso aún no tiene secciones.'}
              </p>
              {canEditBuilder && (
                <button type="button" className="lms-course-module__add-section mt-4" onClick={addSection}>
                  <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
                    add
                  </span>
                  Añadir sección
                </button>
              )}
            </div>
          ) : (
            sections.map((section, sectionIndex) => {
              const isCollapsed = collapsed[section.id];
              return (
                <section
                  key={section.id}
                  className={`lms-course-module lms-course-module--upcoming${section.isVisible === false ? ' opacity-75' : ''}${dragSectionIndex === sectionIndex ? ' lms-course-module--dragging' : ''}${sectionDropIndex === sectionIndex && dragSectionIndex !== sectionIndex ? ' lms-course-module--drop-target' : ''}`}
                  onDragOver={(event) => {
                    if (!canEditBuilder) return;
                    onSectionDragOver(sectionIndex, event);
                  }}
                  onDrop={(event) => {
                    if (!canEditBuilder) return;
                    const marker = event.dataTransfer.getData('text/plain');
                    if (!marker.startsWith('section:')) return;
                    event.preventDefault();
                    const fromIndex = Number(marker.slice('section:'.length));
                    if (!Number.isInteger(fromIndex)) return;
                    moveSection(fromIndex, sectionIndex);
                  }}
                >
                  <div className="lms-course-module__head lms-course-module__head--builder">
                    {canEditBuilder && (
                      <div
                        className="lms-course-module__section-drag"
                        draggable
                        title="Arrastrar para reordenar la sección"
                        aria-label={`Arrastrar sección ${section.title || moduleNumber(sectionIndex)}`}
                        onDragStart={(event) => onSectionDragStart(sectionIndex, event)}
                        onDragEnd={clearSectionDrag}
                      >
                        <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
                          drag_indicator
                        </span>
                      </div>
                    )}
                    <button
                      type="button"
                      className="lms-course-module__head-toggle min-w-0 flex-1 text-left"
                      onClick={() => setCollapsed((prev) => ({ ...prev, [section.id]: !prev[section.id] }))}
                      aria-expanded={!isCollapsed}
                    >
                      <div className="lms-course-module__badge">
                        <span className="lms-course-module__dot" aria-hidden="true" />
                        <span>Módulo {moduleNumber(sectionIndex)}</span>
                        {section.isVisible === false && (
                          <span className="normal-case tracking-normal text-dash-on-surface-variant">· Oculta</span>
                        )}
                      </div>
                      <input
                        type="text"
                        value={section.title}
                        readOnly={!canEditBuilder}
                        disabled={!canEditBuilder}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => {
                          if (!canEditBuilder) return;
                          const title = event.target.value;
                          updateSections((prev) =>
                            prev.map((entry, index) => (index === sectionIndex ? { ...entry, title } : entry)),
                          );
                        }}
                        className="lms-course-module__title-input"
                        aria-label={`Título del módulo ${moduleNumber(sectionIndex)}`}
                      />
                    </button>

                    <div className="flex shrink-0 items-center gap-1">
                      {canEditBuilder && (
                        <div className="relative" ref={menuOpen === section.id ? menuRef : undefined}>
                          <button
                            type="button"
                            className="rounded-lg p-2 text-dash-outline hover:bg-white/80 hover:text-dash-primary"
                            title="Opciones de sección"
                            aria-label="Opciones de sección"
                            onClick={() => setMenuOpen((current) => (current === section.id ? null : section.id))}
                          >
                            <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
                              more_vert
                            </span>
                          </button>
                          {menuOpen === section.id && (
                            <div className="absolute right-0 z-20 mt-1 w-48 rounded-md border border-[#e8e8ea] bg-white py-1 shadow-lg">
                              <button
                                type="button"
                                className="block w-full px-4 py-2 text-left text-sm hover:bg-[#f3f3f6]"
                                onClick={() => {
                                  toggleSectionVisibility(section);
                                  setMenuOpen(null);
                                }}
                              >
                                {section.isVisible === false ? 'Mostrar sección' : 'Ocultar sección'}
                              </button>
                              <button
                                type="button"
                                className="block w-full px-4 py-2 text-left text-sm hover:bg-[#f3f3f6]"
                                onClick={() => {
                                  duplicateSection(section);
                                  setMenuOpen(null);
                                }}
                              >
                                Duplicar sección
                              </button>
                              <button
                                type="button"
                                className="block w-full px-4 py-2 text-left text-sm text-danger-600 hover:bg-[#f3f3f6]"
                                onClick={() => {
                                  setPendingRemoval({ kind: 'section', sectionId: section.id, sectionIndex });
                                  setMenuOpen(null);
                                }}
                              >
                                Eliminar sección
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      <button
                        type="button"
                        className="rounded-lg p-2 text-dash-outline hover:bg-white/80 hover:text-dash-primary"
                        onClick={() => setCollapsed((prev) => ({ ...prev, [section.id]: !prev[section.id] }))}
                        aria-expanded={!isCollapsed}
                        aria-label={isCollapsed ? 'Expandir módulo' : 'Colapsar módulo'}
                      >
                        <span
                          className="material-symbols-outlined text-[1.5rem] text-dash-outline transition-transform"
                          aria-hidden="true"
                        >
                          {isCollapsed ? 'expand_more' : 'expand_less'}
                        </span>
                      </button>
                    </div>
                  </div>

                  {!isCollapsed && (
                    <div className="lms-course-module__body">
                      <div className="lms-course-module__items">
                        {section.items.map((item, itemIndex) => (
                          <div
                            key={item.id}
                            draggable={canEditBuilder}
                            onDragStart={(event) => {
                              if (!canEditBuilder) return;
                              event.stopPropagation();
                              clearSectionDrag();
                              setDragItem({ sectionIndex, itemIndex });
                            }}
                            onDragOver={(event) => {
                              if (!canEditBuilder || !dragItem || dragItem.sectionIndex !== sectionIndex) return;
                              event.preventDefault();
                              event.stopPropagation();
                            }}
                            onDrop={(event) => {
                              if (!canEditBuilder || !dragItem || dragItem.sectionIndex !== sectionIndex) return;
                              event.preventDefault();
                              event.stopPropagation();
                              moveItem(sectionIndex, itemIndex);
                            }}
                          >
                            {item.isLabel ? (
                              <div className="lms-course-module__label-row group">
                                <p className="lms-course-module__label">{item.displayTitle}</p>
                                {canEditBuilder && (
                                  <div className="lms-course-module-item__actions">
                                    <button
                                      type="button"
                                      className="lms-course-module-item__action"
                                      title="Configurar"
                                      aria-label="Configurar etiqueta"
                                      onClick={() => setSelectedItem(item)}
                                    >
                                      <span className="material-symbols-outlined text-[1.125rem]" aria-hidden="true">
                                        edit
                                      </span>
                                    </button>
                                    <button
                                      type="button"
                                      className="lms-course-module-item__action lms-course-module-item__action--danger"
                                      title="Eliminar"
                                      aria-label="Eliminar etiqueta"
                                      onClick={() => setPendingRemoval({ kind: 'item', item, sectionIndex })}
                                    >
                                      <span className="material-symbols-outlined text-[1.125rem]" aria-hidden="true">
                                        delete
                                      </span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="lms-course-module-item group">
                                <div className="lms-course-module-item__main">
                                  {canEditBuilder && (
                                    <button
                                      type="button"
                                      className="lms-course-module-item__drag shrink-0 cursor-grab text-dash-outline active:cursor-grabbing"
                                      title="Arrastrar para reordenar"
                                      aria-label="Arrastrar para reordenar"
                                    >
                                      <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
                                        drag_indicator
                                      </span>
                                    </button>
                                  )}
                                  <div className="lms-course-module-item__icon text-dash-secondary">
                                    <span
                                      className="material-symbols-outlined text-[1.5rem]"
                                      style={{ fontVariationSettings: "'FILL' 1" }}
                                      aria-hidden="true"
                                    >
                                      {moduleIcon(item.typeValue)}
                                    </span>
                                  </div>
                                  <div className="min-w-0">
                                    <h3 className="lms-course-module-item__title">{item.displayTitle}</h3>
                                    <p className="lms-course-module-item__meta">
                                      <span>{item.type}</span>
                                      {item.isVisible === false && <span> · Oculto</span>}
                                    </p>
                                    {(() => {
                                      const restrictionBanners = itemRestrictionBanners(item, sections);
                                      if (restrictionBanners.length === 0) return null;
                                      return (
                                      <div className="mt-2 space-y-1.5">
                                        {restrictionBanners.map((banner, bannerIndex) => (
                                          <p key={bannerIndex} className="text-xs text-dash-on-surface-variant">
                                            {banner.prefix}
                                            {banner.highlight && (
                                              <span className="font-semibold text-dash-primary"> {banner.highlight}</span>
                                            )}
                                          </p>
                                        ))}
                                      </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                                <div className="lms-course-module-item__status lms-course-module-item__actions">
                                  {item.libraryUrl && (
                                    <Link
                                      to={item.libraryUrl}
                                      className="lms-course-module-item__action"
                                      title="Ver en biblioteca"
                                      aria-label="Ver actividad en biblioteca de contenido"
                                    >
                                      <span className="material-symbols-outlined text-[1.125rem]" aria-hidden="true">
                                        visibility
                                      </span>
                                    </Link>
                                  )}
                                  {canEditBuilder && (
                                    <>
                                      <button
                                        type="button"
                                        className="lms-course-module-item__action"
                                        title="Configurar"
                                        aria-label="Configurar actividad"
                                        onClick={() => setSelectedItem(item)}
                                      >
                                        <span className="material-symbols-outlined text-[1.125rem]" aria-hidden="true">
                                          edit
                                        </span>
                                      </button>
                                      <button
                                        type="button"
                                        className="lms-course-module-item__action lms-course-module-item__action--danger"
                                        title="Eliminar"
                                        aria-label="Eliminar actividad"
                                        onClick={() => setPendingRemoval({ kind: 'item', item, sectionIndex })}
                                      >
                                        <span className="material-symbols-outlined text-[1.125rem]" aria-hidden="true">
                                          delete
                                        </span>
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}

                        {canEditBuilder && (
                          <div className="lms-course-module__builder-actions">
                            <button
                              type="button"
                              className="lms-course-module__builder-add lms-course-module__builder-add--primary"
                              onClick={() => setLibrarySectionId(section.id)}
                            >
                              <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
                                add
                              </span>
                              Añadir una actividad o un recurso
                            </button>
                            <button
                              type="button"
                              className="lms-course-module__builder-add"
                              onClick={() => setLabelSectionId(section.id)}
                            >
                              <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
                                label
                              </span>
                              Añadir etiqueta
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </section>
              );
            })
          )}

          {canEditBuilder && sections.length > 0 && (
            <button type="button" className="lms-course-module__add-section" onClick={addSection}>
              <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
                add
              </span>
              Añadir sección
            </button>
          )}
        </div>
      )}

      <AddContentModal
        open={Boolean(librarySectionId)}
        inserting={insertingObject}
        onClose={() => setLibrarySectionId(null)}
        onSelect={insertLearningObject}
        libraryCreateHref={id ? libraryCreateUrl(libraryShowQueryFromCourseBuilder(id)) : '/library/create'}
      />

      <CourseBuilderLabelModal
        open={Boolean(labelSectionId)}
        saving={labelSaving}
        onClose={() => setLabelSectionId(null)}
        onSubmit={insertLabel}
      />

      <ConfirmModal
        open={pendingRemoval !== null}
        title={pendingRemoval?.kind === 'section' ? 'Eliminar sección' : 'Quitar actividad'}
        description={
          pendingRemoval?.kind === 'section'
            ? '¿Eliminar esta sección y todo su contenido?'
            : '¿Quitar esta actividad del curso?'
        }
        confirmLabel={pendingRemoval?.kind === 'section' ? 'Eliminar' : 'Quitar'}
        loading={removing}
        onConfirm={() => {
          if (!pendingRemoval) return;
          if (pendingRemoval.kind === 'section') {
            void deleteSection(pendingRemoval.sectionId, pendingRemoval.sectionIndex);
            return;
          }
          void removeItem(pendingRemoval.item, pendingRemoval.sectionIndex);
        }}
        onClose={() => {
          if (!removing) setPendingRemoval(null);
        }}
      />

      <CourseBuilderItemSettingsModal
        item={selectedItem}
        sections={sections}
        saving={saving}
        onClose={() => setSelectedItem(null)}
        onSave={saveSelectedItemSettings}
      />
    </CourseHubShell>
  );
}
